import * as Turbo from '@hotwired/turbo'

import { dispatcher } from './pub_controller'
import { BulkAction, Confirmable } from '../models'
import { HTMLElementEvent, DispatchEvent } from '../types'
import { Controller } from '@hotwired/stimulus'

export class BulkActionsController extends Controller {
  static targets = ['checkbox', 'menu', 'singleButton', 'singleQuickActions']

  static values = {
    pubChannel: String,
    pubAs: String,
  }

  declare readonly menuTarget: HTMLElement
  declare readonly checkboxTargets: HTMLInputElement[]
  declare readonly singleButtonTargets: HTMLInputElement[]
  declare readonly singleQuickActionsTargets: HTMLInputElement[]
  declare readonly pubChannelValue: string
  declare readonly pubAsValue: string

  checkAll(event: PointerEvent) {
    this.checkboxTargets.forEach((checkbox) => {
      const target = event.target as HTMLInputElement
      if (checkbox.checked !== target.checked) {
        checkbox.click() // This triggers the checkbox's click event, toggling its state
      }
    })
  }

  toggleBulkActions() {
    const hasMultipleSelections = this.selectedValues().length > 1
    this.menuTarget.classList.toggle('visually-hidden', !hasMultipleSelections)
    this.singleButtonTargets.forEach((controller) => {
      controller.querySelector('button').disabled = hasMultipleSelections
    })
    this.singleQuickActionsTargets.forEach((element) => {
      element.classList.toggle('visually-hidden', hasMultipleSelections)
    })
  }

  confirmBulkAction(event: HTMLElementEvent<HTMLElement>) {
    const element = event.target
    const path = event.params['path']
    const body = event.params['body'] || {}
    const confirmationTitle = event.params['confirmationTitle']
    const confirmationMessageTemplate = event.params['confirmationMessage']

    if (!confirmationMessageTemplate) {
      throw (new Error(), 'you must specify a confirmation message template')
    }

    const total = `${this.selectedValues().length}`
    const confirmationMessage = confirmationMessageTemplate.replace(
      'TOTAL',
      total,
    )

    const confirmable = new Confirmable({
      pubChannel: this.pubChannelValue,
      pubAs: this.pubAsValue || 'bulk-action',
      detail: new BulkAction({ path, body }),
      confirmationTitle,
      confirmationMessage,
    })

    // allow buttons to reset correctly on cancel
    element.blur()
    dispatcher(this, 'confirm-operation', this.pubChannelValue, confirmable)
  }

  runBulkAction({ detail: action }: DispatchEvent<BulkAction>) {
    const windowParams = new URLSearchParams(window.location.search)
    const page = windowParams.get('page')
    const items = windowParams.get('items')
    const selections = this.allValues()

    // Prepare the CSRF token
    const csrfMetaTag = document.querySelector('meta[name="csrf-token"]')
    const csrfToken = csrfMetaTag ? csrfMetaTag.getAttribute('content') : null
    fetch(action.path, {
      method: 'PATCH',
      headers: {
        Accept: 'text/vnd.turbo-stream.html, application/json',
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrfToken || '',
      },
      body: JSON.stringify({ ...action.body, page, items, selections }),
    })
      .then((response) => response.text())
      .then((turboStreamResponse) => {
        // If the response is a Turbo Stream template, apply it.
        // `Turbo.renderStreamMessage` will process the stream and
        // update the DOM accordingly.
        if (turboStreamResponse.includes('<turbo-stream')) {
          Turbo.renderStreamMessage(turboStreamResponse)
        } else {
          console.warn(
            'Received a non-turbo-stream response:',
            turboStreamResponse,
          )
        }
      })
      .catch((error) => {
        console.error('Error:', error)
      })
  }

  private selectedValues() {
    return Array.from(this.checkboxTargets)
      .filter((checkbox) => checkbox.checked)
      .map((checkbox) => checkbox.value)
  }

  private allValues() {
    return Object.fromEntries(
      Array.from(this.checkboxTargets).map((checkbox) => [
        checkbox.value,
        checkbox.checked,
      ]),
    )
  }
}
